import { ChoicePicker, type PickerChoice } from '@/components/choice-picker'
import { StatusPill } from '@/components/status-pill'
import type { WarehouseSource, WarehouseSourceChoice, WarehouseStockOption } from '@/api/warehouse-sourcing'
import { formatMoney } from '@/lib/utils'

const MANUFACTURER = 'manufacturer'

const optionKey = (option: { condition: string; used_grade?: string | null }) =>
  `${option.condition}:${option.used_grade ?? ''}`

/**
 * Where a line is ordered from: its manufacturer, or our own warehouse in one of the conditions we
 * hold (USA-1574). Shown only when the warehouse holds the product or the line already pulls from
 * it. Picking a condition saves at once; the server prices the line from the lot.
 */
export function WarehouseSourcePicker({
  value,
  options,
  lineLabel,
  pending = false,
  readOnly = false,
  onChange,
}: {
  value: WarehouseSource | null | undefined
  /** What the warehouse holds of the line's product, per condition. */
  options: readonly WarehouseStockOption[] | undefined
  /** e.g. "Line 3", for the accessible name. */
  lineLabel: string
  pending?: boolean
  readOnly?: boolean
  /** ``label`` is the chosen condition's, e.g. "Open Box", for showing it before the save returns. */
  onChange: (source: WarehouseSourceChoice | null, label: string | null) => void
}) {
  const inStock = (options ?? []).filter((option) => option.available > 0)
  if (!value && !inStock.length) return null

  const pill = value ? (
    <StatusPill tone="progress">Our warehouse · {value.label}</StatusPill>
  ) : (
    <StatusPill tone="neutral">In our warehouse</StatusPill>
  )
  if (readOnly) return pill

  const choices: PickerChoice[] = [
    // A product its maker no longer sells comes only from our shelf.
    ...(value?.only_from_warehouse ? [] : [{ value: MANUFACTURER, label: 'Manufacturer' }]),
    ...inStock.map((option) => ({
      value: optionKey(option),
      label: `Our warehouse · ${option.label}`,
      render: (
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{option.label}</span>
          <span className="text-xs tabular-nums text-muted-foreground">
            Our warehouse · {option.available} left
            {option.unit_net != null && ` · ${formatMoney(option.unit_net)}`}
          </span>
        </span>
      ),
    })),
  ]
  const current = value ? optionKey(value) : MANUFACTURER
  return (
    <ChoicePicker
      title="Order from"
      label={`${lineLabel}: ${value ? `ordered from our warehouse, ${value.label}` : 'ordered from the manufacturer, also in our warehouse'}. Change`}
      value={current}
      choices={choices}
      pending={pending}
      menuClassName="w-64"
      onSelect={(next) => {
        const option = inStock.find((candidate) => optionKey(candidate) === next)
        onChange(
          option
            ? {
                condition: option.condition as WarehouseSourceChoice['condition'],
                used_grade: (option.used_grade ?? null) as WarehouseSourceChoice['used_grade'],
              }
            : null,
          option?.label ?? null,
        )
      }}
    >
      {pill}
    </ChoicePicker>
  )
}
